import { ref } from 'vue'
import type { SwipeTarget } from '@/types/layerTree'

// Module-level singleton state shared between LayersPanel and GenericMapView.

/** raster_type nodeId → selected COG id */
const selectedCogByNode = ref<Map<number, number>>(new Map())

/** selection scopeId → Set of active leaf nodeIds in that scope.
 *  Exclusive scopes keep at most one entry; non-exclusive scopes may have many.
 *  The key is a `SelectionScope.scopeId` from `layerScope.ts`, which is the
 *  outermost group of an unbroken exclusive branch — not necessarily the leaf's
 *  immediate parent group. */
const activeNodesByGroup = ref<Map<number, Set<number>>>(new Map())

/** Set of VectorLayerPage ids that are checked visible */
const visibleVectorLayerIds = ref<Set<number>>(new Set())

/** The layer currently set as the swipe overlay, or null */
const swipeTarget = ref<SwipeTarget | null>(null)

/** Years selected in the year filter; empty set = all years shown */
const selectedYears = ref<Set<number>>(new Set())

/** [west, south, east, north] in EPSG:3857 — set to trigger a map zoom, then cleared */
const pendingZoomExtent = ref<[number, number, number, number] | null>(null)

/** Set of group nodeIds whose map layers are hidden */
const hiddenGroupNodeIds = ref<Set<number>>(new Set())

export function useLayerSelection() {
  function resetForFarmChange() {
    selectedCogByNode.value = new Map()
    activeNodesByGroup.value = new Map()
    visibleVectorLayerIds.value = new Set()
    swipeTarget.value = null
    hiddenGroupNodeIds.value = new Set()
    // selectedYears intentionally preserved across farm switches
  }

  function selectCog(nodeId: number, cogId: number | null) {
    const m = new Map(selectedCogByNode.value)
    if (cogId === null) m.delete(nodeId)
    else m.set(nodeId, cogId)
    selectedCogByNode.value = m
  }

  /**
   * Activate a leaf node within its selection scope (see `layerScope.ts`).
   * - exclusive=true: the scope keeps at most one active node.
   * - exclusive=false: the node is activated independently; siblings unaffected.
   *
   * `allowToggleOff` controls what a *repeat* activation of an already-active
   * node means. It defaults to true (clicking the active control turns it off),
   * but callers that merely re-assert activation as a side effect of picking a
   * sub-selection — a COG date, a default layer — must pass false. Otherwise
   * choosing a different date inside the active node reads as "clicked the
   * active thing again" and silently deactivates the node, which drops its
   * layer from the map and desynchronises the radios.
   */
  function activateNode(groupId: number, nodeId: number, exclusive: boolean, allowToggleOff = true) {
    const m = new Map(activeNodesByGroup.value)
    const current = new Set(m.get(groupId) ?? [])
    if (exclusive) {
      if (allowToggleOff && current.has(nodeId) && current.size === 1) {
        current.clear()
      } else {
        current.clear()
        current.add(nodeId)
      }
    } else {
      if (allowToggleOff && current.has(nodeId)) current.delete(nodeId)
      else current.add(nodeId)
    }
    m.set(groupId, current)
    activeNodesByGroup.value = m
  }

  function isNodeActive(groupId: number, nodeId: number): boolean {
    return activeNodesByGroup.value.get(groupId)?.has(nodeId) ?? false
  }

  function toggleVectorLayer(vectorLayerId: number, visible: boolean) {
    const s = new Set(visibleVectorLayerIds.value)
    if (visible) s.add(vectorLayerId)
    else s.delete(vectorLayerId)
    visibleVectorLayerIds.value = s
  }

  function setSwipeTarget(target: SwipeTarget | null) {
    swipeTarget.value = target
  }

  function setSelectedYears(years: Set<number>) {
    selectedYears.value = years
  }

  function zoomToExtent(extent: [number, number, number, number]) {
    pendingZoomExtent.value = extent
  }

  function clearZoomExtent() {
    pendingZoomExtent.value = null
  }

  function toggleGroupVisibility(nodeId: number) {
    const s = new Set(hiddenGroupNodeIds.value)
    if (s.has(nodeId)) s.delete(nodeId)
    else s.add(nodeId)
    hiddenGroupNodeIds.value = s
  }

  return {
    selectedCogByNode,
    activeNodesByGroup,
    visibleVectorLayerIds,
    swipeTarget,
    selectedYears,
    hiddenGroupNodeIds,
    resetForFarmChange,
    selectCog,
    activateNode,
    isNodeActive,
    toggleVectorLayer,
    setSwipeTarget,
    setSelectedYears,
    pendingZoomExtent,
    zoomToExtent,
    clearZoomExtent,
    toggleGroupVisibility,
  }
}
